import React from 'react'
import Menu, { MenuProps } from 'components/menu'
import Select from 'components/select'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import { EMPTY_MATRIX_SETTINGS } from 'components/spreadsheet/constants/const'
import { IMatrixSettings } from 'types'

function toggle(list: string[], value: string): string[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value]
}

const MatrixMenu: React.FC<MenuProps> = ({ id, menuState, setMenuState, width }) => {
  const { spreadsheetData, setSpreadsheetData } = useDataContext()

  const settings = spreadsheetData.viewDetails.matrixSettings ?? EMPTY_MATRIX_SETTINGS
  const tableColumns = spreadsheetData.tableDetails.columns ?? []
  const viewColumns = spreadsheetData.viewDetails.columns
  const viewColumnIdSet = new Set((viewColumns ?? []).map((c) => c.publicId))
  const columnOptions = tableColumns.map((col) => ({ value: col.publicId, label: col.name }))

  // Ensure the picked column is part of the matrix view's ``columns`` list
  const ensureColumnInView = (columnId: string) => {
    // View columns aren't hydrated yet — dispatching SHOW_COLUMN here would
    // spread an undefined ``viewDetails.columns`` in the reducer and crash
    // (same race as the Sentry render crash this menu was hardened against).
    if (!Array.isArray(viewColumns)) return
    if (viewColumnIdSet.has(columnId)) return
    setSpreadsheetData({ type: 'SHOW_COLUMN', columnId })
  }

  const update = (next: IMatrixSettings) => {
    setSpreadsheetData({ type: 'SET_MATRIX_SETTINGS', matrixSettings: next })
  }

  const onToggleRowLabel = (columnId: string) => {
    if (!columnId) return
    ensureColumnInView(columnId)
    update({ ...settings, rowLabelColumnIds: toggle(settings.rowLabelColumnIds, columnId) })
  }

  const onSelectPivot = (columnId: string) => {
    if (columnId) ensureColumnInView(columnId)
    update({ ...settings, pivotColumnId: columnId || null })
  }

  const onToggleDataField = (columnId: string) => {
    if (!columnId) return
    ensureColumnInView(columnId)
    update({ ...settings, dataFieldColumnIds: toggle(settings.dataFieldColumnIds, columnId) })
  }

  return (
    <Menu id={id} menuState={menuState} setMenuState={setMenuState} width={width ?? 400}>
      <div className="p-4 flex flex-col gap-4" data-testid="matrix-menu">
        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium text-body">Row Labels</span>
          <Select
            multiselect
            options={columnOptions.filter(
              (opt) => opt.value !== settings.pivotColumnId && !settings.dataFieldColumnIds.includes(opt.value)
            )}
            optionsSelected={settings.rowLabelColumnIds}
            onOptionClick={onToggleRowLabel}
            placeholder="Pick one or more row columns…"
            inputId="matrix-row-labels"
          />
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium text-body">Column Label</span>
          <Select
            options={columnOptions.filter(
              (opt) =>
                !settings.rowLabelColumnIds.includes(opt.value) && !settings.dataFieldColumnIds.includes(opt.value)
            )}
            optionsSelected={settings.pivotColumnId ? [settings.pivotColumnId] : []}
            onOptionClick={onSelectPivot}
            placeholder="Pick the column-label column…"
            inputId="matrix-pivot"
            clearable
          />
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium text-body">Data Fields</span>
          <Select
            multiselect
            options={columnOptions.filter(
              (opt) => !settings.rowLabelColumnIds.includes(opt.value) && opt.value !== settings.pivotColumnId
            )}
            optionsSelected={settings.dataFieldColumnIds}
            onOptionClick={onToggleDataField}
            placeholder="Pick one or more data columns…"
            inputId="matrix-data-fields"
          />
        </div>
      </div>
    </Menu>
  )
}

export default MatrixMenu
